import React, { useState, useEffect } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Button, Typography,
  FormGroup, FormControlLabel, Checkbox, Alert, CircularProgress,
} from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { fetchPreferences, updatePreferences } from '../features/Profile/preferenceActions'
import { selectPreferencesData } from '../features/Profile/profileSelectors'

// Self-contained: fetches its own data and dispatches its own updates, so it
// can be dropped in anywhere (Profile's settings menu, Explore's header,
// wherever) without the host screen needing to wire up preferences state.
export default function ManagePreferencesDialog({ open, onClose }) {
  const dispatch = useDispatch()
  const {
    loading: isLoadingPreferences,
    preferences = [],
    userPreferences = [],
    error: preferencesError,
  } = useSelector(selectPreferencesData)

  const [selectedPreferences, setSelectedPreferences] = useState([])

  useEffect(() => {
    if (open) dispatch(fetchPreferences())
  }, [open, dispatch])

  // Sync the checked boxes to the user's saved preferences whenever the
  // dialog is open and that data changes — not just at the instant it
  // opens, since on a screen that's never fetched preferences before (e.g.
  // Explore), the fetch above hasn't resolved yet at that exact moment.
  useEffect(() => {
    if (open) setSelectedPreferences(userPreferences.map((p) => p.id))
  }, [open, userPreferences])

  const enabledPreferences = preferences.filter((p) => p.is_enabled)

  const handleTogglePreference = (prefId) => {
    setSelectedPreferences((prev) =>
      prev.includes(prefId) ? prev.filter((id) => id !== prefId) : [...prev, prefId]
    )
  }

  const handleSave = async () => {
    try {
      await dispatch(updatePreferences(selectedPreferences))
      onClose()
    } catch (error) {
      // Error is already handled in the Redux action
      console.error('Error updating preferences:', error)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <span>Select Preferences</span>
          <Button
            size="small"
            onClick={() => {
              if (selectedPreferences.length === enabledPreferences.length) {
                setSelectedPreferences([])
              } else {
                setSelectedPreferences(enabledPreferences.map((p) => p.id))
              }
            }}
            sx={{ fontSize: '0.75rem' }}
          >
            {selectedPreferences.length === enabledPreferences.length ? 'Deselect All' : 'Select All'}
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent>
        {preferencesError && <Alert severity="error" sx={{ mb: 2 }}>{preferencesError}</Alert>}
        <FormGroup>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {enabledPreferences
              .sort((a, b) => a.order - b.order)
              .map((pref) => {
                const checked = selectedPreferences.includes(pref.id)
                return (
                  <FormControlLabel
                    key={pref.id}
                    control={
                      <Checkbox
                        size="small"
                        checked={checked}
                        onChange={() => handleTogglePreference(pref.id)}
                        color="primary"
                      />
                    }
                    label={<Typography variant="body2">{pref.name}</Typography>}
                    sx={{
                      m: 0,
                      px: 1.5,
                      py: 1,
                      border: '1px solid',
                      borderColor: checked ? 'primary.main' : 'divider',
                      borderRadius: 2,
                      bgcolor: checked ? 'rgba(255,56,92,0.04)' : 'transparent',
                      transition: 'all 0.15s',
                    }}
                  />
                )
              })}
          </Box>
        </FormGroup>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isLoadingPreferences}
          sx={{
            borderRadius: 10,
            background: 'linear-gradient(135deg, #FF385C 0%, #FF6B83 100%)',
            '&:hover': { background: 'linear-gradient(135deg, #D4003A 0%, #FF385C 100%)' },
          }}
        >
          {isLoadingPreferences ? <CircularProgress size={22} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
